// Ultimate Card Game (UCG) - Unit MVP Spotlight: best-unit-of-the-match
// highlight for the game-over screen. See docs/UNIT_MVP_PLAN.md for the full
// design rationale (formula section 3, spotlight decisions section 2.8).
//
// Pure function of MatchStats.unitRecords (plus MatchStats.getUnitLifetime())
// - no UI state, no RNG, no clock - so both lockstep clients would compute
// the identical winner for the identical match (docs/UNIT_MVP_PLAN.md
// section 5), even though the current UI only calls this for the local
// display, never transmits the result.

import { UNIT_MVP } from '../data/balance.js';

function rawImpact(r) {
  return r.damageDealt * UNIT_MVP.weights.damage
    + r.materialCredit * UNIT_MVP.weights.killCredit
    + r.healingDone * UNIT_MVP.weights.healing
    + r.damageTaken * UNIT_MVP.weights.damageTaken;
}

function impactScore(r) {
  return rawImpact(r) / Math.max(r.cost, UNIT_MVP.costFloor);
}

// Which weighted axis actually earned the score - shown in the Spotlight so
// a healer/tank MVP doesn't read as "DMG 0, did nothing" (it earned its spot
// via healing or damage taken, never via dealt damage).
function dominantAxis(r) {
  const weighted = [
    ['damage', r.damageDealt * UNIT_MVP.weights.damage],
    ['killCredit', r.materialCredit * UNIT_MVP.weights.killCredit],
    ['healing', r.healingDone * UNIT_MVP.weights.healing],
    ['damageTaken', r.damageTaken * UNIT_MVP.weights.damageTaken]
  ];
  weighted.sort((a, b) => b[1] - a[1]);
  return weighted[0][1] > 0 ? weighted[0][0] : 'damage';
}

// unit_<n> ids increase monotonically per match (Battlefield.nextUnitId()) -
// used only as the final, deterministic tie-break (docs/UNIT_MVP_PLAN.md
// section 3), never to rank candidates on its own.
function idNum(id) {
  return (typeof id === 'string' ? parseInt(id.replace(/\D/g, ''), 10) : Number(id)) || 0;
}

// teamFilter restricts the candidate pool (the Spotlight only ever shows the
// winning team's own unit, docs/UNIT_MVP_PLAN.md section 2.8) - omit it to
// get the team-agnostic global best, as the formula in section 3 defines it.
export function computeUnitMvp(stats, teamFilter = null) {
  if (!stats?.unitRecords) return null;

  let best = null;
  let bestDamageDealt = 0;
  let bestIdNum = 0;

  for (const [id, r] of stats.unitRecords) {
    if (teamFilter && r.team !== teamFilter) continue;

    const raw = rawImpact(r);
    if (raw <= 0) continue; // never crown a unit that contributed nothing at all
    const lifetime = stats.getUnitLifetime(id);
    if (lifetime < UNIT_MVP.minLifetimeSeconds && raw < UNIT_MVP.minRawImpact) continue;

    const score = impactScore(r);
    const candidateIdNum = idNum(id);
    const better = !best
      || score > best.score
      || (score === best.score && r.damageDealt > bestDamageDealt)
      || (score === best.score && r.damageDealt === bestDamageDealt && candidateIdNum < bestIdNum);

    if (better) {
      best = {
        id,
        score,
        lifetime,
        team: r.team,
        cardData: r.cardData,
        unitType: r.unitType,
        damageDealt: Math.round(r.damageDealt),
        healingDone: Math.round(r.healingDone),
        damageTaken: Math.round(r.damageTaken),
        materialCredit: Math.round(r.materialCredit),
        dominantAxis: dominantAxis(r)
      };
      bestDamageDealt = r.damageDealt;
      bestIdNum = candidateIdNum;
    }
  }

  return best; // null if no eligible candidate (Spotlight hides itself, see main.js)
}
